import { useNavigate } from 'react-router-dom';
import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  Grid,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { useMemo, useState } from 'react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import { PNG } from 'components/Asset/PNG';
import { type HealthScore } from 'types/squares.type';
import type { ServiceView } from 'types/survivalAnalysis.types';
import DetailsComponent from './DetailsComponentV2';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import useAppTheme from 'hooks/useAppTheme';
import { useAppSelector } from 'store/hook';
import { UpcomingPms } from './UpcomingPms';
import { squaresAssetSopData, type AssetSopEntry } from 'data/squares';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const DetailsContent = ({
  selectedAsset,
  healthScores,
}: {
  selectedPlan: string;
  setSelectedPlan: any;
  selectedView: string;
  setSelectedView: any;
  selectedAsset: ServiceView;
  healthScores: HealthScore[];
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const partsHealthComponent = useAppSelector(
    (state) => state.squaresPartsRecommendationsReducer.healthData
  );
  const customerCode = useAppSelector(
    (state) => state.authReducer.customer.code
  );

  const fontColor = palette.textPrimary;

  const [criticality, setCriticality] = useState('All');
  // const [type, setType] = useState('All');

  const handleChange = (event: any) => {
    setCriticality(event.target.value);
  };

  // const handleTypeChange = (event: any) => {
  //   setType(event.target.value);
  // };

  const assetHealthDetails = useMemo(() => {
    if (!Array.isArray(healthScores)) {
      console.error('healthScores not an array');
      return null;
    }
    if (!selectedAsset?.item_id) {
      console.error('selectedAsset or item_id missing');
      return null;
    }

    return (
      healthScores.find(
        (item) => item?.asset_details?.bumper_number === selectedAsset.item_id
      ) ?? null
    );
  }, [healthScores, selectedAsset]);

  const { selectedComponents } = useMemo(() => {
    if (!assetHealthDetails)
      return { selectedRisk: null, selectedComponents: [] };

    if (!Array.isArray(assetHealthDetails?.component_details)) {
      console.error('component_details not array');
      return { selectedRisk: null, selectedComponents: [] };
    }

    const components = assetHealthDetails.component_details;
    const minComponents = 3;

    // Group by risk category
    const highRisk = components.filter(
      (item) => item?.work_order_age_category?.toLowerCase() === 'high'
    );
    const midRisk = components.filter(
      (item) => item?.work_order_age_category?.toLowerCase() === 'med'
    );
    const lowRisk = components.filter(
      (item) => item?.work_order_age_category?.toLowerCase() === 'low'
    );

    let selected: any[] = [];
    let topRisk: string | null = null;

    // Start with high risk - if any exist, add ALL
    if (highRisk.length > 0) {
      selected = [...highRisk];
      topRisk = 'high';
    }

    // If we need more to reach minimum 3, add ALL mid risk
    if (selected.length < minComponents && midRisk.length > 0) {
      selected = [...selected, ...midRisk];
      if (!topRisk) topRisk = 'med';
    }

    // If we still need more to reach minimum 3, add ALL low risk
    if (selected.length < minComponents && lowRisk.length > 0) {
      selected = [...selected, ...lowRisk];
      if (!topRisk) topRisk = 'low';
    }

    return { selectedRisk: topRisk, selectedComponents: selected };
  }, [assetHealthDetails]);

  const filteredByCriticalityComponents = useMemo(() => {
    if (!Array.isArray(selectedComponents)) {
      console.error('selectedComponents is not an array:', selectedComponents);
      return [];
    }

    if (criticality === 'All') return selectedComponents;

    return (
      selectedComponents.filter((item) => item?.criticality === criticality) ??
      []
    );
  }, [selectedComponents, criticality]);

  const pngName =
    selectedAsset?.oem && selectedAsset?.model && selectedAsset?.type
      ? getImageNameByMakeModelAndAsset(
          selectedAsset.oem,
          selectedAsset.model,
          selectedAsset.type,
          'Large'
        )
      : 'fallbackImageSmall';

  const customersSopData: AssetSopEntry[] =
    squaresAssetSopData[customerCode] ?? [];
  const assetSopData = customersSopData.filter(
    (item) => item.asset === selectedAsset.item_id
  );

  return (
    <Box
      sx={{
        border: '1px solid #26364C',
        minHeight: '100px',
        borderRadius: '5px',
        marginTop: '10px',
      }}
    >
      <Grid container spacing={0}>
        <Grid
          item
          lg={3.5}
          sm={12}
          md={12}
          xs={12}
          sx={{
            backgroundColor: enableDarkTheme
              ? palette.boxBackground
              : palette.boxBackground,
          }}
          boxShadow={'initial'}
          display={'flex'}
          flexDirection={'column'}
          gap={1}
          height={'600px'}
        >
          <Box
            sx={{
              borderBottom: '1px solid #26364C',
            }}
          >
            <Box
              sx={{
                padding: '10px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <CalendarMonthIcon
                  sx={{
                    color: '#feb019',
                    marginRight: '10px',
                  }}
                />
                <Typography
                  sx={{
                    color: fontColor,
                    fontSize: '16px',
                    fontWeight: '700',
                  }}
                >
                  {t('planned')}
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: fontColor,
                  fontSize: '12px',
                  fontWeight: '700',
                }}
              >
                {t('upcoming_pms')}
              </Typography>
            </Box>
          </Box>
          <Box overflow={'scroll'}>
            {customerCode === 'drn' ? (
              <>
                {' '}
                <UpcomingPms type="375" assetSopData={assetSopData} />
                <UpcomingPms type="750" assetSopData={assetSopData} />
                <UpcomingPms type="1500" assetSopData={assetSopData} />
              </>
            ) : customerCode === 'buz' ? (
              <>
                {' '}
                <UpcomingPms type="week" assetSopData={assetSopData} />
                <UpcomingPms type="month" assetSopData={assetSopData} />
                <UpcomingPms type="year" assetSopData={assetSopData} />
              </>
            ) : (
              <></>
            )}
          </Box>
        </Grid>
        <Grid item lg={5} sm={12} md={12} xs={12}>
          <Box width={'100%'} height={'500px'}>
            <PNG
              name={pngName}
              alt={selectedAsset.item_id}
              style={{
                height: '100%',
                width: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        </Grid>
        <Grid
          item
          lg={3.5}
          sm={12}
          md={12}
          xs={12}
          sx={{
            backgroundColor: enableDarkTheme
              ? palette.boxBackground
              : palette.boxBackground,
          }}
          boxShadow={'initial'}
          display={'flex'}
          flexDirection={'column'}
          gap={1}
          height={'600px'}
        >
          <Box
            sx={{
              borderBottom: '1px solid #26364C',
            }}
          >
            <Box
              sx={{
                padding: '10px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                    }}
                  >
                    <PanoramaFishEyeIcon
                      sx={{
                        color: palette.primary,
                        marginRight: '10px',
                      }}
                    />
                    <Typography
                      sx={{
                        color: fontColor,
                        fontSize: '16px',
                        fontWeight: '700',
                      }}
                    >
                      {t('ai_insights')}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: palette.grey,
                      fontSize: '12px',
                      fontWeight: '700',
                    }}
                  >
                    {t('symx_analysis')}{' '}
                    <span
                      style={{
                        color: palette.lightSuccess,
                      }}
                    >
                      {t('dm_log_live')}
                    </span>
                  </Typography>
                </Box>
                <Box>
                  <FormControl sx={{ minWidth: 120, marginLeft: '10px' }}>
                    <Select
                      value={criticality}
                      onChange={handleChange}
                      displayEmpty
                      IconComponent={KeyboardArrowDown}
                      sx={{
                        height: '40px',
                        fontSize: '14px',
                        color: '#ffffff',
                        border: '1px solid #26364C',
                        '& .MuiSelect-select': {
                          paddingLeft: '12px',
                          paddingRight: '32px !important',
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#9ca3af',
                          right: '8px',
                        },
                      }}
                    >
                      <MenuItem value="All">{t('all')}</MenuItem>
                      <MenuItem value="high">{t('high')}</MenuItem>
                      <MenuItem value="med">{t('medium')}</MenuItem>
                      <MenuItem value="low">{t('low')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box overflow={'scroll'}>
            {filteredByCriticalityComponents?.length > 0 ? (
              <>
                {filteredByCriticalityComponents?.map((item, index) => {
                  let recommendationText = '';

                  const hasCalendar = !!item.freq_calendar_days;
                  const hasEngine =
                    item.freq_engine_hours &&
                    item.freq_engine_hours !== 'No Recommendation';

                  if (hasCalendar) {
                    recommendationText += `Every ${item.freq_calendar_days}`;
                  }

                  if (hasCalendar && hasEngine) {
                    recommendationText += ' or ';
                  }

                  if (hasEngine) {
                    recommendationText += `Every ${item.freq_engine_hours} hrs`;
                  }

                  if (!hasCalendar && !hasEngine) {
                    recommendationText = 'On Need Basis';
                  }

                  let maintenanceType = item.maintenance_type;
                  const healthData = partsHealthComponent.find(
                    (dt) =>
                      dt.bumper_id === item.bumper_number &&
                      dt.component === item.component &&
                      dt.sub_component === item.sub_component
                  );

                  if (healthData) {
                    maintenanceType =
                      healthData?.recommended_action ?? maintenanceType;
                  }

                  return (
                    <DetailsComponent
                      key={index}
                      enableDarkTheme={enableDarkTheme}
                      isPlanned={false}
                      consequences={[]}
                      actionRecommended={true}
                      component={item.component}
                      subComponent={item.sub_component}
                      iconName="wheelsBlade"
                      age={item.age_in_days}
                      squaresRecommendedDate={item.squares_recommended_date}
                      maintenanceType={maintenanceType}
                      risk={item.work_order_age_category}
                      criticality={item.criticality}
                      recommendationText={recommendationText}
                    />
                  );
                })}
              </>
            ) : (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: fontColor,
                }}
              >
                {t('no_components_for_the_selected_filter')}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              borderTop: '1px solid #26364C',
              padding: '12px',
              textAlign: 'center',
            }}
          >
            <Button
              sx={{
                textTransform: 'none',
                fontSize: '12px',
                height: '45px',
                width: '90%',
                backgroundColor: 'transparent',
                color: fontColor,
                padding: '6px 20px',
                border: '2px dashed rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(13, 71, 161, 0.1)',
                  border: '2px dashed rgba(255, 255, 255, 0.5)',
                },
                fontFamily: 'monospace',
              }}
              onClick={() => {
                navigate('/x-parts/work-order-management', {
                  state: {
                    components: filteredByCriticalityComponents,
                    asset: selectedAsset,
                  },
                });
              }}
            >
              {t('create_work_order')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DetailsContent;
